import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, TrendingUp, TrendingDown, Edit } from 'lucide-react';
import { toast } from 'sonner';

const MonthlyComparison = ({ userId, className = '' }) => {
  const { API, user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    this_month: { matches: 0, wins: 0 },
    last_month: { matches: 0, wins: 0 },
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userId) fetchStats();
  }, [userId]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/monthly-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch monthly stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = () => {
    setForm({
      this_month: { matches: stats?.this_month?.matches || 0, wins: stats?.this_month?.wins || 0 },
      last_month: { matches: stats?.last_month?.matches || 0, wins: stats?.last_month?.wins || 0 },
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API}/admin/users/${userId}/override-monthly-stats`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('עודכן בהצלחה');
      setEditOpen(false);
      fetchStats();
    } catch (e) {
      toast.error('שגיאה בעדכון');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('להחזיר לחישוב אוטומטי? כל השינויים הידניים יימחקו.')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API}/admin/users/${userId}/override-monthly-stats`,
        { reset: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('חזר לחישוב אוטומטי');
      setEditOpen(false);
      fetchStats();
    } catch (e) {
      toast.error('שגיאה');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>השוואה חודשית</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500">טוען...</div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const thisMonth = stats.this_month;
  const lastMonth = stats.last_month;
  const isManual = !!stats.manual_override;

  const getComparison = (current, previous) => {
    if (previous === 0) return { text: '-', color: 'text-gray-500' };
    const diff = current - previous;
    if (diff > 0) return { text: `+${diff}`, color: 'text-green-600', icon: TrendingUp };
    if (diff < 0) return { text: `${diff}`, color: 'text-red-600', icon: TrendingDown };
    return { text: '±0', color: 'text-gray-500' };
  };

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              השוואה חודשית
              {isManual && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">ידני</span>}
            </CardTitle>
            {user && user.is_admin && (
              <Button
                size="sm"
                variant="outline"
                data-testid="edit-monthly-btn"
                onClick={openEdit}
                className="h-7 px-2 border-purple-400 text-purple-600 hover:bg-purple-50"
              >
                <Edit className="w-3.5 h-3.5 ml-1" />
                <span className="text-xs">ערוך</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* החודש הזה */}
            <div className="space-y-3">
              <h3 className="font-bold text-lg text-center text-green-700 border-b-2 border-green-200 pb-2">
                החודש הזה
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-gray-700">משחקים:</span>
                  <span className="font-bold text-green-700">{thisMonth.matches}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-gray-700">ניצחונות:</span>
                  <span className="font-bold text-green-700">{thisMonth.wins}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="text-sm text-gray-700">הפסדים:</span>
                  <span className="font-bold text-green-700">{thisMonth.losses}</span>
                </div>
              </div>
            </div>

            {/* החודש שעבר */}
            <div className="space-y-3">
              <h3 className="font-bold text-lg text-center text-gray-700 border-b-2 border-gray-200 pb-2">
                החודש שעבר
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">משחקים:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-700">{lastMonth.matches}</span>
                    {(() => {
                      const comp = getComparison(thisMonth.matches, lastMonth.matches);
                      return comp.icon && <span className={`text-xs ${comp.color}`}>{comp.text}</span>;
                    })()}
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">ניצחונות:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-700">{lastMonth.wins}</span>
                    {(() => {
                      const comp = getComparison(thisMonth.wins, lastMonth.wins);
                      return comp.icon && <span className={`text-xs ${comp.color}`}>{comp.text}</span>;
                    })()}
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">הפסדים:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-700">{lastMonth.losses}</span>
                    {(() => {
                      const comp = getComparison(thisMonth.losses, lastMonth.losses);
                      return comp.icon && <span className={`text-xs ${comp.color}`}>{comp.text}</span>;
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {editOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setEditOpen(false)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">עריכת השוואה חודשית</h3>
              <button onClick={() => setEditOpen(false)} className="text-2xl">×</button>
            </div>
            <p className="text-xs text-gray-500 mb-4">הפסדים מחושבים אוטומטית כ-(משחקים − ניצחונות).</p>
            
            {['this_month', 'last_month'].map((key) => (
              <div key={key} className="mb-4 p-3 border rounded-lg">
                <h4 className="font-bold mb-2">{key === 'this_month' ? 'החודש הזה' : 'החודש שעבר'}</h4>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-sm">
                    משחקים:
                    <input
                      type="number"
                      min="0"
                      data-testid={`${key}-matches-input`}
                      value={form[key].matches}
                      onChange={(e) => setForm({ ...form, [key]: { ...form[key], matches: parseInt(e.target.value) || 0 } })}
                      className="w-full px-3 py-1 border rounded mt-1"
                    />
                  </label>
                  <label className="text-sm">
                    ניצחונות:
                    <input
                      type="number"
                      min="0"
                      data-testid={`${key}-wins-input`}
                      value={form[key].wins}
                      onChange={(e) => setForm({ ...form, [key]: { ...form[key], wins: parseInt(e.target.value) || 0 } })}
                      className="w-full px-3 py-1 border rounded mt-1"
                    />
                  </label>
                </div>
              </div>
            ))}
            
            <div className="flex justify-between gap-2 mt-4">
              <Button
                onClick={handleReset}
                disabled={saving}
                variant="outline"
                className="text-orange-600 border-orange-400"
                data-testid="reset-monthly-btn"
              >
                חזרה לחישוב אוטומטי
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>ביטול</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-monthly-btn">
                  {saving ? 'שומר...' : 'שמור'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MonthlyComparison;
